<template>
  <!-- Invoice lines table -->
  <table class="gk-table gk-table-invoicelines gk-table--responsive gk-input-light--delete">
    <!-- Table head -->
    <thead>
      <td></td> <!-- Reorder btns -->
      <td class="text-center">{{ $gettext('Quantity') }}</td>
      <td>{{ $gettext('Description') }}</td>
      <td class="text-right">
        <div id="tt-vat-switch">
          <b-dropdown toggle-class="gk-vat-input--toggle" :disabled="!isLinesEmpty" :class="{ 'pointer-none': !isLinesEmpty }">
            <template #button-content>
              <!-- <span v-if="invoice.line_amount_is_gross">
                {{ currentCompany.vat_field_name ? `${unit_price_inc}${currentCompany.vat_field_name})` : unit_price_inc_vat }}
              </span>
              <span v-else>
                {{ currentCompany.vat_field_name ? `${unit_price_ex}${currentCompany.vat_field_name})` : unit_price_ex_vat }}
              </span> -->
              <span>
               {{ unitPriceLabel }}
              </span>
            </template>
            <!-- <b-dropdown-item @click="invoice.line_amount_is_gross = true">
              {{ currentCompany.vat_field_name ? `${inc}${currentCompany.vat_field_name}` : inc_vat }}
            </b-dropdown-item>
            <b-dropdown-item @click="invoice.line_amount_is_gross = false">
              {{ currentCompany.vat_field_name ? `${ex}${currentCompany.vat_field_name}` : ex_vat }}
            </b-dropdown-item> -->

            <b-dropdown-item @click="invoice.line_amount_is_gross = true">
              {{ incOptionLabel }}
            </b-dropdown-item>
            <b-dropdown-item @click="invoice.line_amount_is_gross = false">
              {{ exOptionLabel }}
            </b-dropdown-item>
          </b-dropdown>
        </div>
        <b-tooltip v-if="!isLinesEmpty" target="tt-vat-switch" placement="bottom" triggers="click">
          <div class="tooltip-text">
            {{ switchIncExMsg }}
          </div>
        </b-tooltip>
      </td>

      <!-- Switching between Inc VAT and Ex VAT can cause inconsistencies when 
      there is data on the invoice. Please remove all invoices lines before you can switch. -->

      <td class="text-right">
        <span>{{ totalVatLabel }}</span>
      </td>
      <td class="text-right">{{ taxRateLabel }}</td>
      <td></td>
    </thead>

    <!-- Table body lines table -->
    <!-- <tbody> -->
      <transition-group
        is="draggable"
        v-model="invoice_lines"
        @start="drag=true"
        @end="drag=false"
        handle=".reorder-handle"
        draggable=".draggable-line"
        tag="tbody"
        :name="!drag ? 'flip-list' : null"
        animation="200"
      >
        <tr v-for="(invoice_line, invoice_index) in invoice_lines" :key="'invoice_line-' + invoice_index" :class="{ 'draggable-line': !invoice_line.default, 'default-line': invoice_line.default }">
          <!-- order -->
          <td class="col-reorder">
            <div class="reorder-handle">
              <GekkoButton
                :disabled="invoice_line.default"
                icon="fa-solid fa-grip-dots-vertical"
                variant="link"
                class="p-0 text-unassigned"
              />
            </div>
          </td>

          <!-- Quantity -->
          <td class="col-quantity">
            <LineQuantityInput 
              :id="'invoices_web_invoicelines_linequantity_' + invoice_index"
              v-model.number="invoice_lines[invoice_index].multiplier"
              @compute-net-value="computeNetValue(invoice_index)"
              :disabled="!multiplier"
            />
            <div v-if="checkLineErrors(invoice_index, 'multiplier')" class="input-error">{{ linesErrors[invoice_index]['multiplier'][0] }}</div>
          </td>

          <!-- Description -->
          <td class="col-desc">
            <LineDescriptionInput
              :id="'invoices_web_invoicelines_linedescription_' + invoice_index"
              v-model="invoice_lines[invoice_index].description"
              :line-index="invoice_index"
              :product-disabled="!!(invoice_lines[invoice_index].product)"
              @product-selected="setSelectedProduct"
              @refresh-groups="loadProductGroups"
            />
          </td>

          <!-- Unit price -->
          <td class="col-price">
            <b-input-group class="icon-contained-input-group" :class="{ disabled: !!(invoice_lines[invoice_index].product) }">
              <b-input-group-prepend>
                <b-input-group-text v-html="currencyByCode(invoice.currency).symbol" />
              </b-input-group-prepend>

              <NumberInput 
                :input-id="'invoices_web_invoicelines_lineamount_' + invoice_index"
                v-model.number="invoice_lines[invoice_index].amount"
                :is-disabled="!!(invoice_lines[invoice_index].product)"
                @input="computeNetValue(invoice_index)"
                :class="'text-right'"
              />
              
              <!-- <b-form-input 
                @mousewheel.native="$event.preventDefault()"
                @input="computeNetValue(invoice_index)" 
                v-model.number="invoice_lines[invoice_index].amount"
                type="number" required placeholder="0.00" step="any" min="0" class="text-right"
                :disabled="!!(invoice_lines[invoice_index].product)"
              /> -->

            </b-input-group>
            <div v-if="checkLineErrors(invoice_index, 'amount')" class="input-error">{{ linesErrors[invoice_index]['amount'][0] }}</div>
          </td>

          <!-- Sub/Total (Ex. VAT) -->
          <td class="text-right col-subtotal">
            <div style="padding-top: 7px;">
              <span v-html="currencyByCode(invoice.currency).symbol"></span>{{ invoice_line.subtotal_amount | formatNumber }}
            </div>
          </td>

          <!-- Tax Rate -->
          <td class="col-vat text-right">
            <VatInput 
              :input-id="'invoices_web_invoicelines_linevat_' + invoice_index"
              :value="getVatInputRate(invoice_index)"
              :default-exempt="default_invoice_line.vat_exempt"
              v-if="!invoice.vat_reversed_charge"
              :disabled="!!(invoice_lines[invoice_index].product)"
              @input="(newTaxRate) => { setNewTaxRate(newTaxRate, invoice_index) }"
            />
            <div :id="'invoices_web_invoicelines_isreversecharged' + invoice_index" v-else class="vat-reversed">
              <!-- <font-awesome-icon icon="fa-solid fa-minus" /> -->
              {{ $gettext('VAT Reverse charge') }}
            </div>
          </td>

          <!-- Actions (remove invoice line) -->
          <td>
            <GekkoButton
              :id="'invoices_web_invoicelines_removeline_' + invoice_index" 
              @click="removeInvoiceLine(invoice_index)"
              :disabled="invoice_line.default"
              icon="fa-solid fa-trash-can"
              class="text-danger"
              variant="link"
            />
          </td>
        </tr>
        <tr v-if="showError"><td colspan="6"><div class="d-block invalid-feedback" >{{ $gettext('This field is required.') }}</div></td></tr>
      </transition-group>
    <!-- </tbody> -->

    <!-- Table footer Totals and vat lines -->
    <tfoot>

      <!-- Subtotal -->
      <tr>
        <td colspan="3">
          <GekkoButton
            v-if="importable"
            @click="$emit('show-import')"
            :text="$gettext('Import lines')"
            icon="fa-solid fa-plus"
            variant="link"
            class=""
          />
        </td>
        <td class="text-right">{{ $gettext('Subtotal') }}</td>
        <td class="text-right"><span v-html="currencyByCode(invoice.currency).symbol"></span>{{ subtotal | formatNumber }}</td>
        <td colspan="2"></td>
      </tr>

      <!-- Vat lines -->
      <template v-for="(vat_amount, vat_index, index) in vatTotals" >
        <tr v-if="!invoice.vat_reversed_charge" :key="'vat_totals-' + vat_index">
          <td colspan="3"></td>
          <td class="text-right">{{ vat_index }}% <span>{{ currentCompany.vat_field_name ? currentCompany.vat_field_name : $gettext('VAT') }}</span></td>
          <td class="text-right"><span v-html="currencyByCode(invoice.currency).symbol"></span>{{ vat_amount | formatNumber }}</td>
          <td v-if="index === 0" :rowspan="Object.keys(vatTotals).length" colspan="2" class="vat-reverser">
            <b-form-checkbox id="invoices_web_reversecharge" v-model="invoice.vat_reversed_charge">
              <span>{{ $gettext('Reverse charge') }}</span>
            </b-form-checkbox>
          </td>
        </tr>
      </template>
      
      <tr v-if="invoice.vat_reversed_charge">
        <td colspan="3"></td>
        <td class="text-right">{{ currentCompany.vat_field_name ? currentCompany.vat_field_name : $gettext('VAT') }}</td>
        <td class="text-right"> <font-awesome-icon icon="fa-solid fa-minus" /></td>
        <td colspan="2" class="vat-reverser reversed">
          <b-form-checkbox v-model="invoice.vat_reversed_charge">
            <span>{{ $gettext('Reversed charge') }}</span>
          </b-form-checkbox>
        </td>
      </tr>

      <!-- IRPF (spain) -->
      <tr v-if="currentCompany.country === 'ES' && (selectedCustomer && selectedCustomer.country === 'ES')" class="vat-line">
        <td colspan="3"></td>
        <td class="text-right">
          <div class="d-flex justify-content-end align-items-center" style="gap: 0.5rem;">
            {{ $gettext('IRPF') }}
            <IRPFInput 
              :value="invoice.retention_tax_percentage"
              @input="calculateRetention"
            />
          </div>
        </td>
        <td id="subtotal" class="text-right">
          <span v-html="currencyByCode(invoice.currency).symbol"></span>{{ invoice.retention_tax_amount | formatNumber }}
        </td>
      </tr>

      <!-- Separator total -->
      <tr class="total-tr-separator">
        <td colspan="3"></td>
        <td colspan="2" class="separator">
          <hr>
        </td>
        <td></td>
        <td></td>
      </tr>

      <!-- Total -->
      <tr class="total-tr">
        <td colspan="3"></td>
        <td class="text-right total-total">{{ $gettext('Total') }}</td>
        <td id="invoices_web_invoicelines_total" class="text-right total-total"><span v-html="currencyByCode(invoice.currency).symbol"></span>{{ total | formatNumber }}</td>
        <td colspan="2" class="total-total"></td>
      </tr>

    </tfoot>
  </table>
</template>

<script>
import { mapGetters, mapActions } from "vuex"
import draggable from 'vuedraggable'

import VatInput from "@/components/invoices/ui/VatInput"
import IRPFInput from "@/components/invoices/ui/IRPFInput"
import LineDescriptionInput from "@/components/invoices/ui/LineDescriptionInput"
import LineQuantityInput from "@/components/invoices/ui/LineQuantityInput"
import NumberInput from "@/components/ui/NumberInput"

import GekkoButton from "@/components/GekkoButton"

export default {
  name: 'InvoiceLines',
  components: {
    VatInput,
    GekkoButton,
    LineDescriptionInput,
    LineQuantityInput,
    NumberInput,
    IRPFInput,
    draggable
  },
  props: {
    value: {
      type: Array,
      required: true
    },
    invoice: {
      type: Object,
      required: true
    },
    showError: {
      type: Boolean,
      default: false
    },
    multiplier: {
      type: Boolean,
      default: true
    },
    importable: {
      type: Boolean,
      default: false
    },
    linesErrors: {
      type: Array,
      default: () => []
    },
  },
  data () {
    return {
      lineWatch: null,
      default_invoice_line: {
        order: 0,
        multiplier: 1,
        amount: '',
        line_amount_is_gross: null,
        subtotal_amount: 0,
        tax_rate: null,
        description: '',
        default: true,
        product: null
      },
      drag: false
    }
  },
  computed: {
    ...mapGetters({ 
      currentCompany: "main/getCurrentCompany",
      currencyByCode: "main/getCurrencyByCode",
      selectedCustomer: "invoices/getInvoiceCreateRecipient",
      ueCountryCodes: "main/getUeCountryCodes",
    }),
    invoice_lines: {
      get () { return this.value },
      set (invoice_lines) { this.$emit('input', invoice_lines) }
    },
    vatName () {
      return this.currentCompany && this.currentCompany.vat_field_name
        ? this.currentCompany.vat_field_name
        : null
    },
    unitPriceLabel () {
      if (this.invoice.line_amount_is_gross) {
        if (this.vatName) {
          let text = this.$gettext('Unit price (Inc. %{ vat })')
          return this.$gettextInterpolate(text, { vat: this.vatName })
        }
        return this.$gettext('Unit price (Inc. VAT)')
      } else {
        if (this.vatName) {
          let text = this.$gettext('Unit price (Ex. %{ vat })')
          return this.$gettextInterpolate(text, { vat: this.vatName })
        }
        return this.$gettext('Unit price (Ex. VAT)')
      }
    },
    incOptionLabel () {
      if (this.vatName) {
        let text = this.$gettext('Inc. %{ vat }')
        return this.$gettextInterpolate(text, { vat: this.vatName })
      }
      return this.$gettext('Inc. VAT')
    },
    exOptionLabel () {
      if (this.vatName) {
        let text = this.$gettext('Ex. %{ vat }')
        return this.$gettextInterpolate(text, { vat: this.vatName })
      }
      return this.$gettext('Ex. VAT')
    },
    totalVatLabel () {
      if (this.invoice && this.invoice.line_amount_is_gross) {
        // Inc.
        if (this.vatName) {
          const text = this.$gettext('Total (Inc. %{ vat })')
          return this.$gettextInterpolate(text, { vat: this.vatName })
        }
        return this.$gettext('Total (Inc. VAT)')
      } else {
        // Ex.
        if (this.vatName) {
          const text = this.$gettext('Total (Ex. %{ vat })')
          return this.$gettextInterpolate(text, { vat: this.vatName })
        }
        return this.$gettext('Total (Ex. VAT)')
      }
    },
    taxRateLabel(){
        if (this.vatName) {
          return this.vatName;
        }
        return this.$gettext("Tax Rate");
    },
    switchIncExMsg () {
      if (this.vatName) {
        let text = this.$gettext(
          'Switching between Inc %{ vat } and Ex %{ vat } can cause inconsistencies when there is data on the invoice. Please remove all invoices lines before you can switch.'
        )
        return this.$gettextInterpolate(text, { vat: this.vatName })
      }
      return this.$gettext(
        'Switching between Inc VAT and Ex VAT can cause inconsistencies when there is data on the invoice. Please remove all invoices lines before you can switch.'
      )
    },
    subtotal () {
      let subTotal = 0
      this.invoice_lines.forEach(invoice_line => {
        if (this.invoice.line_amount_is_gross) {
          if (invoice_line.vat_exempt || this.invoice.vat_reversed_charge) {
            subTotal += invoice_line.multiplier * invoice_line.amount
          }else{
            subTotal += invoice_line.multiplier * invoice_line.amount / (1 + invoice_line.tax_rate / 100)
          }
        } else {
          subTotal += Math.round(invoice_line.multiplier * invoice_line.amount * 100) / 100
        }
      })
      return subTotal
    },
    vatTotals () {
      const vat_totals = {}
      this.invoice_lines.forEach(invoice_line => {
        if((invoice_line.amount > 0 || invoice_line.amount < 0) && !invoice_line.vat_exempt) {
          if (!vat_totals[invoice_line.tax_rate]) {
            vat_totals[invoice_line.tax_rate] = 0
          }
          if (this.invoice.line_amount_is_gross) {
            vat_totals[invoice_line.tax_rate] += Math.round(invoice_line.multiplier * invoice_line.amount * 100) / 100 - Math.round((invoice_line.multiplier * invoice_line.amount / ( 1 + invoice_line.tax_rate / 100)) * 100) / 100
          } else {
            vat_totals[invoice_line.tax_rate] += Math.round(invoice_line.multiplier * invoice_line.amount * (invoice_line.tax_rate / 100) * 100) / 100
          }
        }
      })
      return vat_totals
    },
    total () {
      let total = 0
      if (this.invoice.line_amount_is_gross) {
        this.invoice_lines.forEach(invoice_line => {
          total += Math.round(invoice_line.multiplier * invoice_line.amount * 100) / 100
        })
      } else {
        const subtotal = Math.round(parseFloat(this.subtotal) * 100) / 100
        let total_vat = 0

        for (let vat in this.vatTotals) {
          total_vat += Math.round(this.vatTotals[vat] * 100) / 100
        }
        
        if (this.invoice.vat_reversed_charge) total_vat = 0

        total = subtotal + total_vat
      }
      this.calculateRetention()
      if (this.invoice.retention_tax_amount) total += this.invoice.retention_tax_amount

      return total
    },
    watchableLine () {
      return this.invoice_lines[this.invoice_lines.length - 1]
    },
    computedLineToBeWatched () {
      return Object.assign({}, this.watchableLine)
    },
    isLinesEmpty () {
      return this.invoice_lines.length === 1 && this.invoice_lines[0].default
    }
  },
  created () {
    this.loadProductGroups().then(() => {
      this.setDefaults()
      this.addNewLine()
      this.calculateInvoiceLines()
    })
  },
  beforeDestroy () {
    this.removeLineWatch()
    if (this.invoice_lines[this.invoice_lines.length - 1] && this.invoice_lines[this.invoice_lines.length - 1].default) {
      this.removeInvoiceLine(this.invoice_lines.length - 1, true, true)
    }
  },
  methods: {
    ...mapActions({
      loadProductGroups: "invoices/loadProductGroups"
    }),
    /**
     * Adds a new line using a shallow default_invoice_line object and attach a watcher to it
     */
    addNewLine () {
      this.invoice_lines.push({ ...this.default_invoice_line })
      // unwatch the previous invoice_line and add a new one
      this.removeLineWatch()
      this.attachLineWatch()
    },
    addLinesViaImport (lines) {
      // if last invoice line is a default, remove it
      if (this.invoice_lines[this.invoice_lines.length - 1].default) {
        this.removeInvoiceLine(this.invoice_lines.length - 1, true)
      }
      lines.forEach(line => {
        line.multiplier = Math.round(line.multiplier * 100) / 100 // rounding precision error fix

        if (this.invoice.line_amount_is_gross) {

          if (line.product) {
            line.amount = line.total_inc_vat
          }

          if (line.cost_ids && line.cost_ids.length > 0 && !line.vat_exempt) {
            line.amount = line.amount + (line.amount * line.tax_rate / 100)
            line.amount = Math.round(line.amount * 100) / 100
          }
          
        }
        if (this.currentCompany.default_tax_rate_exempt) {
          line.vat_exempt = true
        }
        this.invoice_lines.push(line)
        
      })
      this.calculateInvoiceLines()
      this.addNewLine()
    },
    setSelectedProduct (payload) {
      if (this.invoice.line_amount_is_gross) {
        this.$set(this.invoice_lines[payload.line_index], 'amount', parseFloat(payload.product.total_inc_vat))
      } else {
        this.$set(this.invoice_lines[payload.line_index], 'amount', parseFloat(payload.product.amount))
      }
      this.$set(this.invoice_lines[payload.line_index], 'product', payload.product.id)
      this.$set(this.invoice_lines[payload.line_index], 'line_amount_is_gross', payload.product.amount_is_gross)
      this.$set(this.invoice_lines[payload.line_index], 'tax_rate', payload.product.tax_rate)
      this.$set(this.invoice_lines[payload.line_index], 'vat_exempt', payload.product.vat_exempt)
      this.calculateInvoiceLines()
    },
    /**
     * Removes one invoice_line from this.invoice_lines array
     * Cannot remove last element
     * 
     * @param {number} index invoice_line index to remove
     */
    removeInvoiceLine (index, force = false, destroying = false) {
      if (force || (index < (this.invoice_lines.length - 1))) {
        this.removeLineWatch()
        this.invoice_lines.splice(index, 1)
        // nextTick for NumberInput to complete destroy lifecycle
        if (!destroying) {
          this.$nextTick(() => {
            this.removeLineWatch()
            this.attachLineWatch()
          })
        }
      }
    },
    /**
     * Calls destroy/delete function from a previously created $watcher
     */
    removeLineWatch () {
      if (this.lineWatch) this.lineWatch()
    },
    /**
     * Adds a new $watch() to a invoice_line from this.invoice_lines array
     * 
     * @param {number} line_idx invoice_line index to watch
     */
    attachLineWatch () {
      if (this.invoice_lines.length > 0) {
        this.lineWatch = this.$watch(() => this.computedLineToBeWatched, this.addNewLineViaWatch, { deep: true })
      }
    },
    addNewLineViaWatch (new_val, old_val) {
      if ((new_val.description != old_val.description) && (/^\s*$/.test(new_val.description))) {
        return false
      }

      /**
       * draggable sets a new object on every sort, it triggers the watch due to deep: true
       * need to compare all props to avoid adding extra empty lines
      */
      let changed = false
      for (const prop in old_val) {
        if (Object.prototype.hasOwnProperty.call(old_val, prop) && Object.prototype.hasOwnProperty.call(new_val, prop)) {
          // we are doing this because sometimes(hours import) old_val.amount = null and new_val.amount = ''. which should be treated the same but are actually different values.
          let x = old_val[prop];
          if(old_val[prop] == null){
            x = ''
          }
          let y = new_val[prop];
          if(new_val[prop] == null){
            y = ''
          }
          if (x !== y) {
            changed = true
          }
        }
      }
      if (!changed) {
        return false
      }

      delete this.watchableLine.default
      this.addNewLine()
    },
    /**
     * Sets currentCompany default settings to the default_invoice_line object
     */
    setDefaults () {
      this.default_invoice_line.tax_rate = this.currentCompany.default_tax_rate
      this.default_invoice_line.vat_exempt = this.currentCompany.default_tax_rate_exempt
      if (this.selectedCustomer) {
        if (this.selectedCustomer.country === this.currentCompany.country) {
          this.default_invoice_line.tax_rate = this.currentCompany.default_tax_rate
          this.default_invoice_line.vat_exempt = this.currentCompany.default_tax_rate_exempt
        } else if (this.ueCountryCodes.includes(this.selectedCustomer.country)) {
          this.default_invoice_line.tax_rate = this.currentCompany.default_tax_rate
        } else {
          this.default_invoice_line.tax_rate = 0
        }
      }
    },
    /**
     * Sets a new 'tax_rate' value to given invoice_line via VatInput component @input
     * 
     * @param {number,string} newTaxRate tax_rate value
     * @param {number} invoice_index invoice_line index
     */
    setNewTaxRate (newTaxRate, invoice_index) {
      if (newTaxRate === 'exempt') {
        this.invoice_lines[invoice_index].vat_exempt = true
      }else{
        this.invoice_lines[invoice_index].tax_rate = newTaxRate
        this.invoice_lines[invoice_index].vat_exempt = false
      }
      this.computeNetValue(invoice_index)
    },
    /**
     * Calculates subtotal_amount for the given invoice_line based on multiplier and amount
     * 
     * @param {number} invoice_index index from invoice_lines array
     */
    computeNetValue (invoice_index) {
      let invoice_line = this.invoice_lines[invoice_index]

      if(!this.invoice.vat_reversed_charge){
        if (invoice_line.vat_exempt || invoice_line.tax_rate === 'exempt') {
          invoice_line.tax_rate = 0
        }
        // invoice_line.subtotal_amount = invoice_line.multiplier * invoice_line.amount / (1 + invoice_line.tax_rate / 100)
        invoice_line.subtotal_amount = invoice_line.multiplier * invoice_line.amount
      } else {
        invoice_line.subtotal_amount = invoice_line.multiplier * invoice_line.amount
      }
      invoice_line.subtotal_amount = Math.round(invoice_line.subtotal_amount * 100) / 100; // make sure we have only 2 decimals
    },
    /**
     * Recalculates all invoice_lines subtotal_amounts and net values when selecting 'VAT Reverse Charge' checkbox
     */
    computeReversedCharge () {
      if (this.invoice.vat_reversed_charge) {
        for (let i=0; i < this.invoice_lines.length; i++) {
          this.invoice_lines[i].subtotal_amount = this.invoice_lines[i].amount * (1 - 0.21);
          this.computeNetValue(i);
        }
      } else {
        for (let i=0; i < this.invoice_lines.length; i++) {
          this.invoice_lines[i].subtotal_amount = this.invoice_lines[i].amount / (1 + this.invoice_lines[i].tax_rate / 100);
          this.computeNetValue(i);
        }
      }
    },
    /**
     * Calculates NetValue for all items on this.invoice_lines
     */
    calculateInvoiceLines () {
      for (var i = 0; i < this.invoice_lines.length; i++) {
        // calculate only non default lines
        if (!this.invoice_lines[i].default) {
          // removing empty .00 decimals
          this.invoice_lines[i].multiplier = this.invoice_lines[i].multiplier * 100 / 100
          this.computeNetValue(i)
        }
      }
    },
    /**
     * Sets invoice retention_tax_amount and retention_tax_percentage for spanish companies
     * 
     * @param {number} retention_percentage retention amount
     */
    calculateRetention (retention_percentage) {
      if (this.currentCompany.country !== 'ES' || (this.selectedCustomer && this.selectedCustomer.country !== 'ES')) {
        this.$set(this.invoice, 'retention_tax_amount', null)
        this.$set(this.invoice, 'retention_tax_percentage', null)
        return
      }
      if (retention_percentage) {
        this.$set(this.invoice, 'retention_tax_percentage', retention_percentage)
      }

      let retention_amount = -Math.round(this.subtotal * this.invoice.retention_tax_percentage) / 100
        this.$set(this.invoice, 'retention_tax_amount', retention_amount)

      if (!this.invoice.retention_tax_amount) {
        this.$set(this.invoice, 'retention_tax_amount', 0)
      }
    },
    getVatInputRate (invoice_index) {
      let tax_rate = this.invoice_lines[invoice_index].tax_rate
      if (this.invoice_lines[invoice_index].vat_exempt) {
        tax_rate = 'exempt'
      }
      return tax_rate
    },
    checkLineErrors (line_index, line_input) {
      return this.linesErrors[line_index] && this.linesErrors[line_index][line_input]
    }
  }
}
</script>

<style lang="scss">
  .gk-table-invoicelines {
    background: black;
    .col-reorder {
      padding-left: 0.5rem;
      padding-right: 0.5rem;
      .reorder-handle {
        .btn { 
          font-size: 14px;
          color: $colorUnassigned;
          &:hover {
            color: $colorBlue;
          }
        }
      }
    }
    .col-quantity { 
      min-width: 125px;
      width: 125px; 
      input { text-align: center; }
      padding-right: 0;
      padding-left: 0;
      .quantity-times {
        padding-left: 0.75rem;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
      }
    }
    .col-desc { min-width: 150px; width: 100%; }
    .col-price { min-width: 100px; width: 150px; }
    .col-vat { 
      min-width: 120px; 
      width: 130px; 
      text-align: center;
      .vat-reversed { color: $colorUnassigned; }
    }

    .form-control { padding: 0.25rem 0.5rem; }
    input[type=number]::-webkit-inner-spin-button, 
    input[type=number]::-webkit-outer-spin-button { 
      -webkit-appearance: none; 
      margin: 0; 
    }
    .input-group-prepend {
      .input-group-text {
        padding: 0.25rem 0.5rem;
        // padding-right: 0;
        color: $colorTextLight;
      }
    }
    .gk-input-light {
      .input-group-text {
        padding: 0.25rem 0.5rem;
        padding-right: 0;
        color: $colorTextLight;
      }
    }
    .input-group-prepend + input { border-left: 0; }

    thead {
      display: table-header-group !important;
      border-bottom: 1px solid $colorBorderDash;
      td {
        padding: 0.75rem;
        font-size: 14px;
        button { font-size: 14px; }
        white-space: nowrap;
        color: $colorTextBase;
        // background: #faf9fb;
        &:first-child { border-top-left-radius: $radiusLg; }
        &:last-child { border-top-right-radius: $radiusLg; }
      }
    }
    tbody {
      tr { 
        &.sortable-chosen {
          background: white;
          // opacity: 0.5;
        }
        &.sortable-ghost {
          opacity: 0;
        }
        cursor: initial !important;
        box-shadow: none !important;
        &.default-line {
          .col-subtotal {
            opacity: 0.3;
          }
        }
        &.draggable-line {
          .reorder-handle {
            opacity: 1;
          }
        }
        td { 
          vertical-align: top;
          .input-error {
            color: $colorDanger;
            font-size: 14px;
          }
        }
      }
    }
    tfoot, tbody {
      tr:first-child td { padding-top: 1rem; }
      tr:last-child td { padding-bottom: 1rem; }
    }

    tfoot {
      tr {
        &:first-child { border-top: 1px solid $colorBorderDash; }
        td { padding: 0.5rem 0.75rem; }
        .vat-reverser {
          &.reversed {
          }
          font-size: 1rem;
        }
        &.total-tr-separator {
          td { padding: 0; }
          .separator {
            padding: 0.5rem 0;
            hr {
              padding: 0; margin: 0;
              border-top: 1px solid $colorBorderDash;
            }
          }
        }
        &.total-tr {
          .total-total {
            font-size: 20px;
            color: black;
          }
        }
      }
    }
  }
</style>
